import React from 'react';
import { Card, CardContent, Stack, Typography, CircularProgress, IconButton, Box } from '@mui/material';
import { TrendingUp, MoreVert } from '@mui/icons-material';
import { MetricTile } from './types';
import { WaveScore } from '../../../components/shared/WaveScore';

// Inline trend indicator (replaces chip) matching screenshot style
function TrendIndicator({ pct }: { pct?: number }) {
  if (pct == null) return null;
  const positive = pct >= 0;
  return (
    <Stack direction="row"
      alignItems="center"
      sx={{ gap: '4px', color: positive ? 'success.main' : 'error.main' }}>
      <TrendingUp fontSize="inherit"
        sx={{ transform: positive ? 'none' : 'rotate(180deg)' }} />
      <Typography variant="textMD"
        fontWeight={600}>{`${positive ? '+' : ''}${pct.toFixed(1)}%`}</Typography>
    </Stack>
  );
}

export function WaveScoreCard({ metric }: { metric: MetricTile & { segments?: { value: number; color: string }[]; description?: string } }) {
  const max = metric.max ?? 1;
  const label = metric.format ? metric.format(metric.value) : `${Math.round((metric.value / max) * 100)}%`;
  return (
    <Card variant="outlined"
      sx={{ display: 'flex', borderRadius: 3 }}>
      <CardContent sx={{ p: '20px', pl: '18px', display: 'flex', alignItems: 'stretch', gap: '20px', width: '100%' }}>

        <WaveScore
          index={1}
          score={metric.value}
          maxScore={max} />
        <Box flex={1}
          display="flex"
          flexDirection="column"
          justifyContent={'space-between'}
          minWidth={0}>
          <Box display="flex"
            alignItems="flex-start"
            justifyContent="space-between"
            gap={1}>
            <Typography variant="textMD"
              fontWeight={700}
              color="text.primary"
              noWrap>{metric.label}</Typography>
            <IconButton size="small"
              edge="end"
              sx={{ mt: '-4px', mr: '-4px' }}
              aria-label="more options">
              <MoreVert fontSize="small" />
            </IconButton>
          </Box>
          {metric.description && (
            <Typography variant="textXS"
              color="text.secondary"
              sx={{ mt: '2px', lineHeight: 1.35, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {metric.description}
            </Typography>
          )}
          <Box mt={'10px'}
            display="flex"
            alignItems="flex-end"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1}>
            <Typography component="h5"
              fontWeight={700}
              lineHeight={1}>{label}</Typography>
            <TrendIndicator pct={metric.trendPct} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
